/**
 * InspectorContext — owns the side-panel state for the Agent of Agents page.
 *
 * Why a context (not parent state): both the AoAChatPanel (left) and the
 * AoaInspectorPanel (right) need to read AND mutate this state — the
 * chat dispatches new proposals into the queue when SSE events arrive,
 * the panel resolves them and pops the next from the queue, and any
 * inline marker (chat or panel header) can call setActive(...) to swap
 * focus. Lifting the state and threading 6+ props through both halves
 * was uglier than a small context.
 *
 * Design decisions (user-confirmed 2026-06-10):
 *
 *   - Queue mechanics for proposals: new propose_* events join a FIFO
 *     queue when there's already something in the active slot. The
 *     "N waiting" badge in the panel header surfaces that.
 *
 *   - Simulations (send_test_message_to_agent) REPLACE the active slot
 *     unconditionally — they're interactive and persistent (the eval
 *     conversation lives in the DB), so the previously displayed item
 *     just collapses back into its chat marker.
 *
 *   - The panel can be collapsed; the width is persisted in localStorage
 *     so reopening a session feels right.
 *
 *   - One active slot at a time; everything else is reachable via the
 *     chat's inline markers (proposals) or via clicking a
 *     send_test_message_to_agent tool block (simulations).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useArtifacts, { type UseArtifactsResult } from '@modules/conversations/hooks/useArtifacts';
import type { AoAProposal, ProposalResolution } from '../types';
import { fileMayTakeScreen, type ArtifactActivationSource } from '../fileActivation';

/**
 * Signature of the auto-continue listener owned by the chat panel. The
 * panel cannot run it itself because the auto-continue logic depends on
 * the chat's streaming machinery — instead the chat registers a listener
 * (via the ref below) and the panel fires it after `updateProposal`.
 */
export type ProposalResolvedListener = (
  resolution: ProposalResolution,
  updated: AoAProposal,
) => void;

/**
 * Fired by the simulation panel when the user has settled which tenant to
 * simulate under and wants the run to begin. The chat panel implements it by
 * sending a normal turn to the Agent of Agents, which then drives the
 * conversation with the target agent itself.
 */
/** A panel handing a plain-language request back to the meta-agent, which
 *  answers it the normal way — including raising an approval card. Used by the
 *  file panel's "link this file" actions: linking is a real change, so it must
 *  go through a card the user accepts, not a silent API call from the panel. */
export type ChatRequestListener = (message: string) => void;

/**
 * Discriminated union of what the inspector can show. Designed so the
 * panel's render switch matches on `.kind` and downstream code never
 * has to ask "is this a proposal or a simulation".
 */
export type InspectorItem =
  | {
      kind: 'proposal';
      proposal: AoAProposal;
    }
  | {
      kind: 'simulation';
      eval_conversation_id: string;
      target_chatbot_id: string | null;
      target_agent_name: string;
      /** Optional anchor: when the user clicked a specific
       *  send_test_message_to_agent tool block, scroll the panel to
       *  this message id and highlight it. Re-used on click of any
       *  later tool-block click. */
      scrollToMessageId?: string | null;
    }
  | {
      /** A file the meta-agent wrote. Rendered by the SAME ArtifactPanel a
       *  regular chat uses - it is the same kind of object, so it gets the
       *  same preview, version dropdown and download, not a lookalike. */
      kind: 'artifact';
      artifact_file_id: string;
    };

interface InspectorState {
  /** Currently displayed item, or null when the panel is empty. */
  active: InspectorItem | null;
  /** FIFO of proposals waiting their turn. Only proposals queue —
   *  simulations replace the active slot. */
  proposalQueue: AoAProposal[];
  /** Which half of the panel is showing.
   *
   *  'cards' = the decision queue, 'files' = the artifact previews. They are
   *  SEPARATE surfaces rather than one slot they take turns owning: a file
   *  preview and a card waiting for an answer are different kinds of thing,
   *  and making them compete meant one always hid the other. */
  panelTab: 'cards' | 'files';
  setPanelTab: (tab: 'cards' | 'files') => void;
  /** A LIBRARY file the agent opened for the user (open_library_file) —
   *  previewed on the Files tab above the artifact list. Distinct from
   *  artifacts: this is an existing stored file, shown read-only with a
   *  download button, not something the agent wrote this conversation. */
  libraryPreview: { file_id: string; file_name: string; file_type?: string } | null;
  setLibraryPreview: (
    preview: { file_id: string; file_name: string; file_type?: string } | null,
  ) => void;
  /** Whether the panel is collapsed (chat takes the full width). */
  collapsed: boolean;
  /** Panel width as a CSS fraction of the page (0..1). Persists. */
  widthFraction: number;
  /** Monotonic counter bumped every time a simulation event lands for
   *  the SAME currently-active eval conversation. The SimulationPanelView
   *  watches this and refetches messages on bump. Why: a multi-turn eval
   *  fires send_test_message_to_agent 2-3 times with the same
   *  eval_conversation_id; the panel stays mounted (key by id) and
   *  needs a signal to pull in the freshly-persisted assistant turns. */
  simulationRefreshNonce: number;
}

interface InspectorActions {
  /** Push a new proposal into the inspector. If nothing is active,
   *  it becomes active immediately. Otherwise it joins the queue. */
  pushProposal: (proposal: AoAProposal) => void;
  /** Per-eval optimistic state — user message text we want to show
   *  immediately when the AoA fires send_test_message_to_agent but the
   *  tool's result hasn't come back yet. Keyed by eval_conversation_id;
   *  the SimulationPanelView reads its own eval's entry and renders it
   *  as a temp user bubble. Cleared when the tool result arrives (the
   *  refetch replaces it with the persisted version). */
  pendingUserMessageByEvalId: Record<string, string>;
  /** Which eval conversations currently have a send_test_message_to_agent
   *  tool in flight. The panel renders a "agent is typing…" indicator
   *  for those, so the wait between firing the tool and getting the
   *  reply feels live instead of frozen. */
  toolInFlightEvalIds: Set<string>;
  setPendingUserMessage: (evalConversationId: string, content: string) => void;
  clearPendingUserMessage: (evalConversationId: string) => void;
  setToolInFlight: (evalConversationId: string, inFlight: boolean) => void;
  /** The target agent's reply as it streams in for an AoA-driven turn, per
   *  eval conversation. The SimulationPanelView renders it as a live bubble
   *  and drops it the moment the persisted row lands. */
  simulationDraftByEvalId: Record<string, string>;
  appendSimulationDraft: (evalConversationId: string, chunk: string) => void;
  clearSimulationDraft: (evalConversationId: string) => void;
  /** Set a specific proposal as active (e.g. when the user clicks an
   *  inline marker in the chat). If a different proposal was active,
   *  it goes back to the front of the queue so the user doesn't lose
   *  their place. Removes the target proposal from the queue if it
   *  was queued. */
  setActiveProposal: (proposal: AoAProposal) => void;
  /** Promote a simulation to the active slot. Replaces whatever was
   *  there (per the design rule). */
  setActiveSimulation: (sim: Extract<InspectorItem, { kind: 'simulation' }>) => void;
  /** Promote a file the meta-agent wrote to the active slot. Replaces
   *  whatever was there. `expand: false` fills the slot without opening the
   *  panel - used when restoring an old conversation's files, where throwing
   *  the panel open on every page load would be an interruption nobody asked
   *  for. The collapsed rail's badge still shows something is waiting. */
  setActiveArtifact: (
    artifactFileId: string,
    /** Who asked (default `user`, a click). Whether the file may take the
     *  screen is `fileMayTakeScreen` (../fileActivation). */
    opts?: { expand?: boolean; source?: ArtifactActivationSource },
  ) => void;
  /** Live artifact state for this conversation, shared by the chat (which
   *  feeds it SSE events) and the panel (which renders it). Owned here for the
   *  same reason the proposal queue is: both halves read AND write it. */
  artifacts: UseArtifactsResult;
  /** Called by the panel when the user accepts / rejects / cancels.
   *  Pops the next proposal from the queue, if any. */
  closeActive: () => void;
  /** Mutate an existing proposal in the queue / active slot after a
   *  proposal_resolved event arrives. Keeps the visible state in
   *  sync with the DB. */
  updateProposal: (proposal: AoAProposal) => void;
  /** Toggle collapse / expand. Width is preserved across collapses. */
  toggleCollapsed: () => void;
  setCollapsed: (collapsed: boolean) => void;
  /** Adjust panel width via the drag handle. */
  setWidthFraction: (frac: number) => void;
  /** Ref the chat panel writes to so the inspector can fire the chat's
   *  auto-continue logic after a proposal resolves. Stored on a ref
   *  (not in state) so writing it doesn't re-render every consumer. */
  proposalResolvedListenerRef: React.MutableRefObject<ProposalResolvedListener | null>;
  /** Ref the chat panel writes to so any panel can ask the meta-agent for
   *  something in words, and let it respond with the right tool + card. */
  chatRequestListenerRef: React.MutableRefObject<ChatRequestListener | null>;
  /** The META conversation currently on screen. Cards need it to bind what
   *  they create back to the conversation that asked for it — the simulation
   *  tenant card sets it as the eval's parent so the run shows up under this
   *  conversation's evals rather than floating unparented. A ref, not state:
   *  it changes only on navigation and no render depends on it. */
  metaConversationIdRef: React.MutableRefObject<string>;
  /** Wipe everything (active item, queue, staged file uploads) — fired
   *  by the AoA page when the user switches to a different conversation.
   *  Otherwise the panel keeps showing the previous chat's proposal,
   *  and any staged file uploads carry across (confusing). */
  resetForNewConversation: () => void;
  /** Per-proposal staged file uploads. Lives outside the ProposalCard
   *  so navigating away from a file-manager card and back doesn't lose
   *  what the user already dropped in. Keyed by proposal_id; pure
   *  in-memory (File objects can't be serialised to localStorage). */
  stagedFilesByProposalId: Record<string, File[]>;
  setStagedFiles: (proposalId: string, files: File[]) => void;
  clearStagedFiles: (proposalId: string) => void;
}

type InspectorContextValue = InspectorState & InspectorActions;

const InspectorContext = createContext<InspectorContextValue | null>(null);

const LS_WIDTH_KEY = 'aoa.inspector.widthFraction';
// NOTE: collapsed state is INTENTIONALLY not persisted. Per user
// request (2026-06-11) the panel starts hidden on every page load and
// only auto-expands when the first proposal / simulation arrives.
// Persisting it across reloads broke that contract.
const DEFAULT_WIDTH_FRACTION = 0.42;     // ~42% of the chat-pane area
const MIN_WIDTH_FRACTION = 0.28;
const MAX_WIDTH_FRACTION = 0.66;

function readStoredFraction(): number {
  try {
    const raw = localStorage.getItem(LS_WIDTH_KEY);
    if (!raw) return DEFAULT_WIDTH_FRACTION;
    const n = parseFloat(raw);
    if (!isFinite(n)) return DEFAULT_WIDTH_FRACTION;
    return Math.max(MIN_WIDTH_FRACTION, Math.min(MAX_WIDTH_FRACTION, n));
  } catch {
    return DEFAULT_WIDTH_FRACTION;
  }
}

// Clear any previously-saved preference so users who saved
// `collapsed=false` before this change don't keep getting an open panel.
// One-shot best-effort cleanup; cheap on every mount.
function purgeLegacyCollapsedPreference(): void {
  try { localStorage.removeItem('aoa.inspector.collapsed'); } catch { /* ignore */ }
}

export const InspectorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [active, setActive] = useState<InspectorItem | null>(null);
  const [proposalQueue, setProposalQueue] = useState<AoAProposal[]>([]);
  // Always start collapsed. No localStorage read — see comment on
  // LS_WIDTH_KEY above.
  const [panelTab, setPanelTabState] = useState<'cards' | 'files'>('cards');
  const [libraryPreview, setLibraryPreviewState] = useState<
    { file_id: string; file_name: string; file_type?: string } | null
  >(null);
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    purgeLegacyCollapsedPreference();
    return true;
  });
  const [widthFraction, setWidthFractionState] = useState<number>(() => readStoredFraction());
  // Per-proposal staged files. Holds the actual `File` objects the user
  // dropped into a file-manager card so leaving the card and coming back
  // restores them. Cleared on apply OR on conversation switch.
  const [stagedFilesByProposalId, setStagedFilesByProposalId] = useState<
    Record<string, File[]>
  >({});
  // Refresh nonce — bumped by setActiveSimulation when the new eval id
  // matches the one already on screen, telling the panel to refetch.
  const [simulationRefreshNonce, setSimulationRefreshNonce] = useState(0);
  // Per-eval optimistic state — see InspectorActions.
  const [simulationDraftByEvalId, setSimulationDraftByEvalId] = useState<Record<string, string>>({});
  const [pendingUserMessageByEvalId, setPendingUserMessageByEvalId] = useState<
    Record<string, string>
  >({});
  const [toolInFlightEvalIds, setToolInFlightEvalIds] = useState<Set<string>>(
    () => new Set(),
  );
  // The conversation's artifacts. Exactly the hook a regular chat uses, so the
  // meta-agent's files load, version, preview and download identically.
  const artifacts = useArtifacts();

  // Persist user preferences when they change. We persist on every
  // change rather than only on unmount so the user gets their layout
  // back even if they hard-refresh.
  useEffect(() => {
    try { localStorage.setItem(LS_WIDTH_KEY, String(widthFraction)); } catch { /* ignore */ }
  }, [widthFraction]);
  // Collapsed state is NOT persisted — see LS_WIDTH_KEY comment.

  // Use a ref to read the latest active synchronously inside callbacks
  // — needed because pushProposal / setActiveProposal / setActiveSimulation
  // all branch on "what's currently active" and we can't trust a closed-
  // over state value mid-batch.
  const activeRef = useRef(active);
  useEffect(() => { activeRef.current = active; }, [active]);

  // Chat-owned auto-continue listener. The chat panel registers its
  // callback via an effect on mount; we keep it on a ref so consumer
  // identity doesn't bounce.
  const proposalResolvedListenerRef = useRef<ProposalResolvedListener | null>(null);
  const chatRequestListenerRef = useRef<ChatRequestListener | null>(null);
  const metaConversationIdRef = useRef<string>('');

  // Helper — any code path that puts a NEW item in the active slot
  // (proposal_emitted promote, queue auto-advance, marker click,
  // simulation auto-open) should auto-uncollapse the panel so the user
  // actually sees what just landed. The user explicitly asked
  // (2026-06-11) for the side panel to start hidden and only open on
  // first activity. Individual setters call this; they no longer need
  // their own setCollapsed calls.
  const autoExpandOnActivity = useCallback(() => {
    setCollapsedState((c) => (c ? false : c));
  }, []);

  // Helper: two proposals describe the "same action" if they target
  // the same field on the same entity using the same tool. The AoA
  // sometimes re-fires the same propose_* (e.g. after a long pause)
  // and we don't want that to create a duplicate card behind the one
  // the user is already looking at.
  const isEquivalentProposal = (a: AoAProposal, b: AoAProposal): boolean => {
    if (a.proposal_id === b.proposal_id) return true;
    // Mirror the backend's supersede scoping: only EDIT-SLOT proposals
    // (field AND target both set) can be "the same action". Create /
    // attach / link tools have no unique slot — several distinct pending
    // cards for the same tool are legitimate at once (e.g. two skills
    // drafted in one turn) and must never dedupe/replace each other.
    if ((a.field ?? null) === null || (a.target_entity_id ?? null) === null) return false;
    if (a.tool_name !== b.tool_name) return false;
    if (a.target_entity_type !== b.target_entity_type) return false;
    if ((a.target_entity_id ?? null) !== (b.target_entity_id ?? null)) return false;
    if ((a.field ?? null) !== (b.field ?? null)) return false;
    return true;
  };

  // Same action AND same content — the true re-fire case. An equivalent
  // proposal with DIFFERENT content is a REVISION (user gave feedback in
  // chat instead of clicking Reject, model drafted again) and must win the
  // active slot, not be dropped behind the stale draft.
  const isSameContent = (a: AoAProposal, b: AoAProposal): boolean => {
    try {
      return (
        JSON.stringify(a.new_value ?? null) === JSON.stringify(b.new_value ?? null) &&
        JSON.stringify(a.proposed_payload ?? null) === JSON.stringify(b.proposed_payload ?? null)
      );
    } catch {
      return false;
    }
  };

  // pushProposal decision tree:
  //
  //   active slot is...           →  what we do
  //   ──────────────────────────────────────────────────────────
  //   empty (null)                →  promote new proposal immediately
  //   a RESOLVED proposal         →  replace immediately (the user
  //                                  was viewing history; a fresh
  //                                  live proposal takes precedence
  //                                  over read-only history)
  //   a simulation                →  replace immediately (live > history)
  //   a PENDING proposal          →  queue (don't yank the card the
  //                                  user is mid-decision on) UNLESS
  //                                  the new proposal is an equivalent
  //                                  action — then drop it (the user
  //                                  is already looking at the same
  //                                  card).
  //   the SAME proposal           →  no-op (already shown)
  const pushProposalImpl = useCallback((proposal: AoAProposal) => {
    const cur = activeRef.current;
    // Exact same proposal id — already showing, nothing to do.
    if (cur && cur.kind === 'proposal' && cur.proposal.proposal_id === proposal.proposal_id) {
      return;
    }
    // Equivalent action already active and still pending:
    //   - identical content → drop the newcomer. User feedback
    //     2026-06-12: an unanswered card re-arriving in the queue after
    //     a few seconds felt like pestering.
    //   - different content → it's a REVISION; replace the active card
    //     so the user is looking at the draft the model just wrote, not
    //     the one they talked it out of. (The backend has already
    //     superseded the old row; its resolved event retires the marker.)
    if (
      cur &&
      cur.kind === 'proposal' &&
      cur.proposal.status === 'pending' &&
      isEquivalentProposal(cur.proposal, proposal)
    ) {
      if (isSameContent(cur.proposal, proposal)) {
        return;
      }
      const item: InspectorItem = { kind: 'proposal', proposal };
      activeRef.current = item;
      setActive(item);
      // A card is a question — show the half of the panel it lives in.
      // Without this a card raised while the user is looking at a file would
      // sit unseen behind the Files tab, and the backend would auto-reject it
      // seconds later as "user moved on".
      setPanelTabState('cards');
      autoExpandOnActivity();
      return;
    }
    const shouldPromote =
      cur === null ||
      cur.kind === 'simulation' ||
      (cur.kind === 'proposal' && cur.proposal.status !== 'pending');
    if (shouldPromote) {
      const item: InspectorItem = { kind: 'proposal', proposal };
      activeRef.current = item;
      setActive(item);
      // A card is a question — show the half of the panel it lives in.
      // Without this a card raised while the user is looking at a file would
      // sit unseen behind the Files tab, and the backend would auto-reject it
      // seconds later as "user moved on".
      setPanelTabState('cards');
      autoExpandOnActivity();
      return;
    }
    // Active is a pending proposal that's NOT equivalent — queue the
    // newcomer. Dedupe rule matches the active-slot check above:
    // identical content → drop; a queued equivalent with different
    // content is a stale draft — replace it in place with the revision.
    setProposalQueue((q) => {
      if (q.some((p) => isEquivalentProposal(p, proposal) && isSameContent(p, proposal))) {
        return q;
      }
      const stale = q.findIndex((p) => isEquivalentProposal(p, proposal));
      if (stale !== -1) {
        const next = [...q];
        next[stale] = proposal;
        return next;
      }
      return [...q, proposal];
    });
  }, [autoExpandOnActivity]);

  const setActiveProposal = useCallback((proposal: AoAProposal) => {
    const current = activeRef.current;
    // If clicking a marker for the SAME proposal that's already active,
    // it's a no-op — caller probably just wants to ensure focus.
    if (current && current.kind === 'proposal' && current.proposal.proposal_id === proposal.proposal_id) {
      return;
    }
    // Remove the clicked proposal from the queue if it was there.
    setProposalQueue((q) => {
      const filtered = q.filter((p) => p.proposal_id !== proposal.proposal_id);
      // If the currently active item is a different PENDING proposal,
      // push it to the FRONT of the queue so the user doesn't lose it.
      if (
        current &&
        current.kind === 'proposal' &&
        current.proposal.status === 'pending' &&
        current.proposal.proposal_id !== proposal.proposal_id
      ) {
        // Avoid duplicating if somehow already there.
        if (!filtered.some((p) => p.proposal_id === current.proposal.proposal_id)) {
          return [current.proposal, ...filtered];
        }
      }
      return filtered;
    });
    const next: InspectorItem = { kind: 'proposal', proposal };
    activeRef.current = next;
    setActive(next);
    autoExpandOnActivity();
  }, [autoExpandOnActivity]);

  const setActiveSimulation = useCallback((sim: Extract<InspectorItem, { kind: 'simulation' }>) => {
    const current = activeRef.current;
    // Continuation case — same eval conversation as what's already on
    // screen. Don't remount the panel (would lose scroll position,
    // cancel in-flight reads, flicker). Just bump the refresh nonce so
    // the panel pulls in the newly-persisted turn from the DB. Also
    // update the scroll anchor in case the caller wants to jump to a
    // specific message.
    // Live activity is shown where it happens. The panel has two tabs, and
    // anything that opens a file — an auto-selected artifact, a Library
    // preview the meta-agent read — flips it to Files. Nothing flipped it
    // back, so a simulation that was already active kept receiving turns
    // into a panel nobody could see: the meta-agent read every reply while
    // the user watched the second and third never arrive (2026-09-19).
    setPanelTabState('cards');
    setLibraryPreviewState(null);
    if (
      current &&
      current.kind === 'simulation' &&
      current.eval_conversation_id === sim.eval_conversation_id
    ) {
      if (sim.scrollToMessageId && sim.scrollToMessageId !== current.scrollToMessageId) {
        const next = { ...current, scrollToMessageId: sim.scrollToMessageId };
        activeRef.current = next;
        setActive(next);
      }
      setSimulationRefreshNonce((n) => n + 1);
      autoExpandOnActivity();
      return;
    }
    // Fresh sim — replace the active slot. Per design rule, any active
    // pending proposal goes back to the front of the queue so the user
    // can resolve it after they finish the simulation.
    setProposalQueue((q) => {
      if (
        current &&
        current.kind === 'proposal' &&
        current.proposal.status === 'pending'
      ) {
        if (!q.some((p) => p.proposal_id === current.proposal.proposal_id)) {
          return [current.proposal, ...q];
        }
      }
      return q;
    });
    activeRef.current = sim;
    setActive(sim);
    // Reset the nonce so the freshly-mounted panel starts at 0; it'll
    // bump from its own messages-fetched effect on subsequent turns.
    setSimulationRefreshNonce(0);
    autoExpandOnActivity();
  }, [autoExpandOnActivity]);

  const setActiveArtifact = useCallback((artifactFileId: string,
                                        opts?: { expand?: boolean; source?: ArtifactActivationSource }) => {
    const expand = opts?.expand !== false;
    const current = activeRef.current;
    // Already showing this file - nothing to do. Without this, every edit's
    // artifact_render would re-promote the same item and stamp on a card the
    // user had just opened from the timeline.
    if (current && current.kind === 'artifact'
        && current.artifact_file_id === artifactFileId) {
      if (expand) autoExpandOnActivity();
      return;
    }
    if (!fileMayTakeScreen(opts?.source ?? 'user', current)) return;
    // Files live in their OWN tab, so showing one no longer costs the cards
    // their slot: the pending card stays exactly where it was and the user
    // flips back to it whenever they like. Nothing is queued or displaced.
    // A Library preview is drawn over the files, so it closes: the file asked
    // for is what the tab must show.
    artifacts.setActiveId(artifactFileId);
    setLibraryPreviewState(null);
    setPanelTabState('files');
    if (expand) autoExpandOnActivity();
  }, [autoExpandOnActivity, artifacts]);

  const closeActive = useCallback(() => {
    // Pop the next STILL-PENDING proposal from the queue, if any.
    //
    // Skipping the decided ones matters: a card can be answered while it sits
    // in the queue (the turn's auto-reject, or a resolve from another tab), and
    // popping it anyway put a question the user had already answered back on
    // their screen — the "it keeps coming back" report. A decided card is
    // history; it stays reachable from its timeline marker, not from here.
    setProposalQueue((q) => {
      const pendingIdx = q.findIndex((p) => p.status === 'pending');
      if (pendingIdx === -1) {
        activeRef.current = null;
        setActive(null);
        return [];
      }
      const next: InspectorItem = { kind: 'proposal', proposal: q[pendingIdx] };
      activeRef.current = next;
      setActive(next);
      return q.slice(pendingIdx + 1);
    });
  }, []);

  const updateProposal = useCallback((proposal: AoAProposal) => {
    // REJECTED (incl. superseded-by-revision) cards are CLEANED, not kept:
    // drop from the queue, and if it's the active card, auto-advance to
    // the next queued card (or clear the panel). The timeline marker still
    // records the rejection and can re-open it as history. User feedback
    // 2026-08-03: rejected cards lingering in the panel/queue read as
    // still-open questions.
    if (proposal.status === 'rejected') {
      setProposalQueue((q) => {
        const rest = q.filter((p) => p.proposal_id !== proposal.proposal_id);
        const cur = activeRef.current;
        if (cur && cur.kind === 'proposal' && cur.proposal.proposal_id === proposal.proposal_id) {
          if (rest.length > 0) {
            const [head, ...tail] = rest;
            const next: InspectorItem = { kind: 'proposal', proposal: head };
            activeRef.current = next;
            setActive(next);
            return tail;
          }
          activeRef.current = null;
          setActive(null);
        }
        return rest;
      });
      return;
    }
    // ANY resolved card leaves the QUEUE, not just rejected ones. An approved
    // card that stayed queued got popped later by closeActive and reappeared
    // as a question the user had already answered. It is still shown in the
    // ACTIVE slot below when it is the card they just answered — seeing it
    // flip to "Applied" is the confirmation — but it must never be waiting in
    // line behind something else.
    if (proposal.status !== 'pending') {
      setProposalQueue((q) => q.filter((p) => p.proposal_id !== proposal.proposal_id));
    }
    // If the active item is this proposal, refresh it. The SSE
    // `proposal_resolved` path hands us a STUB (only id + status are
    // real) — merge onto the existing proposal instead of replacing it,
    // so the card keeps its content while flipping status.
    const merge = (p: AoAProposal): AoAProposal =>
      (proposal.tool_name ? proposal : { ...p, status: proposal.status, resolved_at: proposal.resolved_at });
    setActive((cur) => {
      if (cur && cur.kind === 'proposal' && cur.proposal.proposal_id === proposal.proposal_id) {
        const next: InspectorItem = { kind: 'proposal', proposal: merge(cur.proposal) };
        activeRef.current = next;
        return next;
      }
      return cur;
    });
    // Refresh queued copies as well so a `proposal_resolved` reflects in
    // the queue chip too.
    setProposalQueue((q) =>
      q.map((p) => (p.proposal_id === proposal.proposal_id ? merge(p) : p)),
    );
  }, []);

  const setPanelTab = useCallback((tab: 'cards' | 'files') => setPanelTabState(tab), []);
  const setLibraryPreview = useCallback(
    (preview: { file_id: string; file_name: string; file_type?: string } | null) => {
      setLibraryPreviewState(preview);
      if (preview) {
        setPanelTabState('files');
        setCollapsedState((c) => (c ? false : c));
      }
    },
    [],
  );
  const toggleCollapsed = useCallback(() => setCollapsedState((c) => !c), []);
  const setCollapsed = useCallback((c: boolean) => setCollapsedState(c), []);
  const setWidthFraction = useCallback((frac: number) => {
    const clamped = Math.max(MIN_WIDTH_FRACTION, Math.min(MAX_WIDTH_FRACTION, frac));
    setWidthFractionState(clamped);
  }, []);

  // Hard reset — fired by the page when activeId switches. We DO keep
  // the user's panel width + collapsed preference (those are UI
  // preferences, not per-conversation state).
  const resetForNewConversation = useCallback(() => {
    activeRef.current = null;
    setActive(null);
    setProposalQueue([]);
    setStagedFilesByProposalId({});
    setSimulationRefreshNonce(0);
    setPendingUserMessageByEvalId({});
    setSimulationDraftByEvalId({});
    setToolInFlightEvalIds(new Set());
    artifacts.reset();
    setLibraryPreviewState(null);
  }, [artifacts]);

  const setStagedFiles = useCallback((proposalId: string, files: File[]) => {
    setStagedFilesByProposalId((prev) => ({ ...prev, [proposalId]: files }));
  }, []);

  const clearStagedFiles = useCallback((proposalId: string) => {
    setStagedFilesByProposalId((prev) => {
      if (!(proposalId in prev)) return prev;
      const next = { ...prev };
      delete next[proposalId];
      return next;
    });
  }, []);

  // Optimistic simulation state — set when the AoA fires
  // send_test_message_to_agent and we want the panel to react instantly
  // (before the tool result lands 5-10 seconds later).
  const setPendingUserMessage = useCallback(
    (evalConversationId: string, content: string) => {
      setPendingUserMessageByEvalId((prev) => ({ ...prev, [evalConversationId]: content }));
    },
    [],
  );

  const clearPendingUserMessage = useCallback((evalConversationId: string) => {
    setPendingUserMessageByEvalId((prev) => {
      if (!(evalConversationId in prev)) return prev;
      const next = { ...prev };
      delete next[evalConversationId];
      return next;
    });
  }, []);

  const setToolInFlight = useCallback(
    (evalConversationId: string, inFlight: boolean) => {
      setToolInFlightEvalIds((prev) => {
        const has = prev.has(evalConversationId);
        if (inFlight && has) return prev;
        if (!inFlight && !has) return prev;
        const next = new Set(prev);
        if (inFlight) next.add(evalConversationId);
        else next.delete(evalConversationId);
        return next;
      });
    },
    [],
  );

  const appendSimulationDraft = useCallback((evalConversationId: string, chunk: string) => {
    if (!chunk) return;
    setSimulationDraftByEvalId((prev) => ({
      ...prev,
      [evalConversationId]: (prev[evalConversationId] || '') + chunk,
    }));
  }, []);

  const clearSimulationDraft = useCallback((evalConversationId: string) => {
    setSimulationDraftByEvalId((prev) => {
      if (!(evalConversationId in prev)) return prev;
      const next = { ...prev };
      delete next[evalConversationId];
      return next;
    });
  }, []);

  const value: InspectorContextValue = useMemo(() => ({
    active,
    proposalQueue,
    collapsed,
    panelTab,
    setPanelTab,
    libraryPreview,
    setLibraryPreview,
    widthFraction,
    simulationRefreshNonce,
    pushProposal: pushProposalImpl,
    setActiveProposal,
    setActiveSimulation,
    setActiveArtifact,
    artifacts,
    closeActive,
    updateProposal,
    toggleCollapsed,
    setCollapsed,
    setWidthFraction,
    proposalResolvedListenerRef,
    chatRequestListenerRef,
    metaConversationIdRef,
    resetForNewConversation,
    stagedFilesByProposalId,
    setStagedFiles,
    clearStagedFiles,
    pendingUserMessageByEvalId,
    toolInFlightEvalIds,
    setPendingUserMessage,
    clearPendingUserMessage,
    setToolInFlight,
    simulationDraftByEvalId,
    appendSimulationDraft,
    clearSimulationDraft,
  }), [
    active, proposalQueue, collapsed, widthFraction, simulationRefreshNonce,
    pushProposalImpl, setActiveProposal, setActiveSimulation, setActiveArtifact,
    artifacts,
    closeActive, updateProposal, toggleCollapsed, setCollapsed, setWidthFraction,
    panelTab, setPanelTab, libraryPreview, setLibraryPreview,
    resetForNewConversation, stagedFilesByProposalId, setStagedFiles, clearStagedFiles,
    pendingUserMessageByEvalId, toolInFlightEvalIds,
    setPendingUserMessage, clearPendingUserMessage, setToolInFlight,
    simulationDraftByEvalId, appendSimulationDraft, clearSimulationDraft,
  ]);

  return (
    <InspectorContext.Provider value={value}>
      {children}
    </InspectorContext.Provider>
  );
};

export function useInspector(): InspectorContextValue {
  const ctx = useContext(InspectorContext);
  if (!ctx) {
    throw new Error('useInspector must be used inside an <InspectorProvider>');
  }
  return ctx;
}

/**
 * Read-only helpers exposed for tests / debugging. Pure functions over
 * the public state shape so a unit test can build a fixture state and
 * call them without touching React.
 */
export const inspectorClamps = {
  MIN: MIN_WIDTH_FRACTION,
  MAX: MAX_WIDTH_FRACTION,
  DEFAULT: DEFAULT_WIDTH_FRACTION,
};
